/**
 * Operação do restaurante (#332): modo de abertura (override "forçar
 * aberto/fechado", migração 0080) e payload do formulário de horário.
 * Funções puras (testadas em operations.test.ts) usadas pelo Branding.
 */

export type OpenMode = 'auto' | 'open' | 'closed'

/** Modo da config; respostas antigas (antes da 0080) caem em 'auto'. */
export function openModeOf(cfg: { open_mode?: OpenMode | null } | undefined | null): OpenMode {
  return cfg?.open_mode ?? 'auto'
}

/**
 * Payload do seletor: o modo sincroniza o interruptor no PUT (open → true,
 * closed → false, auto → true) — enviado junto para o estado ficar coerente
 * mesmo com uma SPA antiga em outro separador.
 */
export function openModePayload(mode: OpenMode): { open_mode: OpenMode; is_open: boolean } {
  return { open_mode: mode, is_open: mode !== 'closed' }
}

/**
 * Horário a guardar: os 7 dias (o PUT exige todos); dia vazio = null/null
 * ('' vira null). Dia meio preenchido NÃO é apagado: o servidor responde 400
 * INCOMPLETE_HOURS. O servidor não grava linha para dias vazios — guardar tudo vazio deixa a tabela vazia (GET devolve [] e o menu
 * trata como "sem restrição de horário"), em vez de 7 linhas "fechado".
 */
export type DayHourInput = {
  day_of_week: number
  open_time: string | null
  close_time: string | null
}

export function hoursPayload(
  days: readonly { open_time: string | null; close_time: string | null }[],
): DayHourInput[] {
  // dia meio preenchido segue tal como está: o PUT responde 400
  // INCOMPLETE_HOURS e o painel mostra o erro, em vez de apagar o dia em
  // silêncio (notas do QA da #335/#342)
  return days.map((h, day_of_week) => ({
    day_of_week,
    open_time: h.open_time || null,
    close_time: h.close_time || null,
  }))
}

type Translate = (key: string, vars?: Record<string, string>) => string

/** Dias da semana (0=domingo) de `details.days` do INCOMPLETE_HOURS. */
function incompleteDaysOf(details: unknown): number[] {
  const days = (details as { days?: unknown } | null | undefined)?.days
  if (!Array.isArray(days)) return []
  return days.filter((d): d is number => Number.isInteger(d) && d >= 0 && d <= 6)
}

/**
 * Mensagem do erro ao guardar o horário, no idioma do painel (notas do QA):
 * INCOMPLETE_HOURS vem do servidor com a mensagem em português — o painel
 * traduz pelo código e lista os dias pelo nome traduzido (Intl.ListFormat:
 * «segunda e quinta», "Monday and Thursday"). Outros erros seguem a mensagem
 * do servidor.
 */
export function hoursErrorMessage(
  err: { message: string; code?: string; details?: unknown },
  t: Translate,
  lang: string,
): string {
  if (err.code !== 'INCOMPLETE_HOURS') return err.message
  const names = incompleteDaysOf(err.details).map((d) => t(`admin.branding.hours.day_${d}`))
  if (names.length === 0) return err.message
  let days = names.join(', ')
  try {
    days = new Intl.ListFormat(lang, { style: 'long', type: 'conjunction' }).format(names)
  } catch {
    /* idioma inválido para o Intl: lista simples */
  }
  return t('admin.branding.hours_incomplete', { days })
}
